// ── recentFillTape — the combined Kalshi + Polymarket display tape ──
//
// Display-only merge for the Recent Fills rail and the /fills popup (#457).
// Both venues keep their own read path: Kalshi arrives through accountStore's
// ingestFillBatch/processFill (which own dedup, ordering and sounds), Polymarket
// through the authenticated relay history route. This module never touches either —
// it takes two already-loaded arrays and answers one question: what are the
// most recent N fills, newest first, across both venues?
//
// Deliberately free of Zustand, React, query clients, sound helpers, the
// current time and any side effect, so the ordering and identity rules below
// are testable as plain data. Poly rows must never enter processFill: that
// path owns Kalshi audio, and #457 adds no Polymarket sounds.

import type { KalshiFill } from '@/types/kalshi';
import type { PolyRelayFill } from '@/lib/polymarket/relay';

/** One bound for the whole tape, shared with accountStore's Kalshi slice and
 *  with the Poly request limit. Merge-then-slice is only correct while each
 *  venue supplies at least this many candidates, so all three must move
 *  together — that is why this constant is imported rather than repeated. */
export const RECENT_FILL_LIMIT = 20;

export type RecentFillTapeRow = {
  /** Venue-qualified display identity. Kalshi fill ids and Poly ledger ids are
   *  issued by different systems, so an unqualified id could collide across
   *  venues and silently drop one of the two fills. */
  key: `kalshi:${string}` | `polymarket:${string}`;
  venue: 'kalshi' | 'polymarket';
  /** The venue's own fill id, for display/debugging only. Null on legacy poly
   *  rows, which is exactly why it cannot be the key. */
  sourceFillId: string | null;
  ticker: string;
  /** Relay-persisted poly market identity. Null for Kalshi, whose label is
   *  derived from the ticker by describeMarket at render time. */
  marketLabel: string | null;
  marketSport: string | null;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  priceCents: number;
  executedAt: string;
  executedAtMs: number;
};

/** Unparseable times sort to the tail rather than being dropped or read as
 *  "now". A row the tape cannot place is still a fill the trader made, and
 *  defaulting it to the current instant would float it above real activity.
 *  Neither venue is expected to reach this today (the Task 3 decoder rejects a
 *  bad poly executed_at, and accountStore coalesces a missing Kalshi
 *  created_at), so this is a module invariant, not a live case. */
function toMillis(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

function fromKalshi(fill: KalshiFill): RecentFillTapeRow {
  return {
    key: `kalshi:${fill.fillId}`,
    venue: 'kalshi',
    sourceFillId: fill.fillId,
    ticker: fill.ticker,
    marketLabel: null,
    marketSport: null,
    side: fill.side,
    action: fill.action,
    count: fill.count,
    priceCents: fill.price,
    executedAt: fill.createdAt,
    executedAtMs: toMillis(fill.createdAt),
  };
}

function fromPoly(fill: PolyRelayFill): RecentFillTapeRow {
  return {
    key: `polymarket:${fill.ledger_id}`,
    venue: 'polymarket',
    sourceFillId: fill.fill_id,
    ticker: fill.ticker,
    marketLabel: fill.market_label,
    marketSport: fill.market_sport,
    side: fill.side,
    action: fill.action,
    // Counts and cent prices pass through untouched: poly sizes are fractional
    // and its prices are sub-cent, so rounding here would misreport the fill.
    count: fill.count,
    priceCents: fill.price_cents,
    executedAt: fill.executed_at,
    executedAtMs: toMillis(fill.executed_at),
  };
}

/**
 * Merge both venues into one bounded, newest-first tape.
 *
 * The limit applies to the UNION, after sorting: taking N per venue first
 * would drop rows that belong in the combined top N whenever one venue is
 * busier than the other.
 */
export function buildRecentFillTape(
  kalshiFills: readonly KalshiFill[],
  polyFills: readonly PolyRelayFill[],
  limit: number = RECENT_FILL_LIMIT,
): RecentFillTapeRow[] {
  const byKey = new Map<string, RecentFillTapeRow>();
  for (const fill of kalshiFills) {
    const row = fromKalshi(fill);
    if (!byKey.has(row.key)) byKey.set(row.key, row);
  }
  for (const fill of polyFills) {
    const row = fromPoly(fill);
    if (!byKey.has(row.key)) byKey.set(row.key, row);
  }

  return [...byKey.values()]
    .sort((a, b) => {
      if (a.executedAtMs !== b.executedAtMs) return b.executedAtMs - a.executedAtMs;
      // Equal instants are common (a single order filling in one sweep), so
      // the tie-break has to be stable and independent of input order or the
      // rail would reshuffle on every poll.
      return a.key < b.key ? 1 : a.key > b.key ? -1 : 0;
    })
    .slice(0, limit);
}
